import React from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const SeoDetailBrandModuleMobileComponent = ({ data }) => {
  const { title, description, footer, cards = [] } = data || {};

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',

        px: '16px',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
          fontSize: '2rem',
          lineHeight: '2.125rem',
          letterSpacing: '-0.03em',
          color: '#162F3B',
          textAlign: 'center',
          whiteSpace: 'pre-line',
          '& p': { margin: 0 },
        }}
        dangerouslySetInnerHTML={{ __html: title || '' }}
      />

      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '1.25rem',
          lineHeight: '1.5rem',
          letterSpacing: '-0.03em',
          color: '#162F3B',
          textAlign: 'center',
          maxWidth: '343px',
          mt: '16px',
          mb: '32px',
          whiteSpace: 'pre-line',
          '& p': { margin: 0 },
        }}
        dangerouslySetInnerHTML={{ __html: description || '' }}
      />

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {cards.map((card, index) => {
          const linkUrl = card.link?.url || card.ctaUrl || card.href;
          const linkLabel = card.link?.label || card.ctaText || card.linkText;
          const hasCardLink = Boolean(linkUrl && linkLabel);

          return (
            <ScrollFadeWrapper key={index} delay={index * 0.1}>
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '34px',
                  boxShadow: '0px 6px 16px #162F3B1F, 0px 1px 2px #A2ACB11F',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '224px',
                    backgroundColor: '#A2ACB1',
                  }}
                >
                  {card.image && (
                    <Image
                      width={358}
                      height={224}
                      src={card.image}
                      alt={
                        card.imageAlt || card.title || `Brand card ${index + 1}`
                      }
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    p: '20px 16px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      lineHeight: '1.75rem',
                      letterSpacing: '-0.03em',
                      color: '#162F3B',
                      textAlign: 'center',
                      mb: '12px',
                      '& p': { margin: 0 },
                    }}
                    dangerouslySetInnerHTML={{ __html: card.title || '' }}
                  />

                  <Box
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.002em',
                      color: '#162F3B',
                      '& p': { m: 0, mb: '1em' },
                      '& p:last-of-type': { mb: 0 },
                      '& strong': { fontWeight: 700 },
                      '& ul': {
                        m: 0,
                        mt: '1em',
                        pl: 0,
                        listStylePosition: 'inside',
                      },
                      '& a': {
                        color: '#C7CB00',
                        textDecoration: 'underline',
                        fontWeight: 700,
                      },
                      '& a:hover': {
                        opacity: 0.85,
                      },
                      '& table': {
                        width: '100% !important',
                        borderRadius: '24px',
                        overflow: 'hidden',
                        borderCollapse: 'separate !important',
                        borderSpacing: '0 !important',
                        fontSize: '0.75rem',
                      },
                      '& table th, & table td': {
                        borderWidth: '1px !important',
                        borderStyle: 'solid !important',
                        borderColor: '#D6DADB !important',
                      },
                      '& table thead th': {
                        borderTop: '1px solid #D6DADB !important',
                        borderBottom: '1px solid #D6DADB !important',
                      },
                      '& table thead th:not(:first-of-type)': {
                        borderLeft: '0 !important',
                      },
                      '& table thead th:not(:last-of-type)': {
                        borderRight: '0 !important',
                      },
                      '& table th:first-of-type': {
                        borderTopLeftRadius: '24px',
                      },
                      '& table th:last-of-type': {
                        borderTopRightRadius: '24px',
                      },
                      '& table tr:last-of-type td:first-of-type': {
                        borderBottomLeftRadius: '24px',
                      },
                      '& table tr:last-of-type td:last-of-type': {
                        borderBottomRightRadius: '24px',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: card.description || '' }}
                  />

                  {hasCardLink && (
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: '12px',
                      }}
                    >
                      <Typography
                        component="a"
                        href={linkUrl}
                        target={card.link?.target || '_self'}
                        rel={
                          (card.link?.target || '_self') === '_blank'
                            ? 'noopener noreferrer'
                            : undefined
                        }
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          lineHeight: '1.25rem',
                          letterSpacing: '-0.002em',
                          color: '#C7CB00',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {linkLabel}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </ScrollFadeWrapper>
          );
        })}
      </Box>

      <Typography
        sx={{
          textAlign: 'center',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: '1.25rem',
          letterSpacing: '-0.002em',
          color: '#162F3B',
          maxWidth: '343px',
          mt: '24px',
        }}
      >
        {footer}
      </Typography>
    </Box>
  );
};

export default SeoDetailBrandModuleMobileComponent;
